import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageHeader, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import type { StatusTone } from '@ui-kit';
import { useRetentionRuns } from '../hooks/useRetentionRuns';
import type { ApiError, RetentionRunStatus } from '../types/retentionAdmin';

const DEFAULT_PAGE_SIZE = 20;

function runStatusTone(s: RetentionRunStatus): StatusTone {
  if (s === 'SUCCESS') return 'success';
  if (s === 'ABORTED' || s === 'FAILED') return 'danger';
  if (s === 'SKIPPED') return 'warning';
  return 'neutral';
}

function SkeletonRow({ cols }: { cols: number }) {
  return (
    <tr>
      {Array.from({ length: cols }).map((_, i) => (
        <td
          key={i}
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <div
            style={{
              height: 12,
              width: `${50 + (i * 11) % 40}%`,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

/**
 * RetentionRunHistoryPage — 실행 이력 (/retention-admin/history)
 * 5상태: loading / empty / error / default
 */
export function RetentionRunHistoryPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [policyNameFilter, setPolicyNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);

  const [appliedFilters, setAppliedFilters] = useState({
    policyName: '',
    status: '',
    page: 0,
  });

  const runsQuery = useRetentionRuns({
    policyName: appliedFilters.policyName || undefined,
    status: appliedFilters.status || undefined,
    page: appliedFilters.page,
    size: DEFAULT_PAGE_SIZE,
    sort: 'startedAt,desc',
  });

  const pageData = runsQuery.data;
  const isLoading = runsQuery.isLoading;
  const isError = runsQuery.isError;
  const isEmpty = !isLoading && !isError && (pageData?.empty ?? false);
  const showTable = !isLoading && !isError && !isEmpty && !!pageData;

  const handleSearch = useCallback(() => {
    setPage(0);
    setAppliedFilters({ policyName: policyNameFilter, status: statusFilter, page: 0 });
  }, [policyNameFilter, statusFilter]);

  const handleReset = useCallback(() => {
    setPolicyNameFilter('');
    setStatusFilter('');
    setPage(0);
    setAppliedFilters({ policyName: '', status: '', page: 0 });
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    setAppliedFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  function formatDateTime(iso: string) {
    return new Date(iso).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  const TABLE_HEADERS = useMemo(
    () => [
      t('retentionAdmin.history.column.startedAt'),
      t('retentionAdmin.history.column.policy'),
      t('retentionAdmin.history.column.trigger'),
      t('retentionAdmin.history.column.status'),
      t('retentionAdmin.history.column.counts'),
      t('retentionAdmin.history.column.duration'),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language]
  );

  const selectStyle = {
    padding: 'var(--spacing-2) var(--spacing-3)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    background: 'var(--color-surface-elevated)',
    color: 'var(--color-text-primary)',
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };

  return (
    <div style={{ padding: 0 }}>
      {/* 페이지 헤더 */}
      <PageHeader
        title={t('retentionAdmin.history.title')}
        breadcrumb={<span>{t('retentionAdmin.history.breadcrumb')}</span>}
        actions={
          <button
            type="button"
            onClick={() => navigate('/retention-admin')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: 'var(--spacing-2) var(--spacing-3)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            ← {t('retentionAdmin.history.button.back')}
          </button>
        }
      />

      {/* 필터 바 */}
      <div
        role="search"
        aria-label={t('retentionAdmin.history.title')}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--spacing-3)',
          padding: 'var(--spacing-3) var(--spacing-4)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--color-surface-muted)',
          marginBottom: 'var(--spacing-4)',
          marginTop: 'var(--spacing-4)',
          alignItems: 'flex-end',
        }}
      >
        {/* 정책명 */}
        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-1)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {t('retentionAdmin.history.filter.policyName')}
          <input
            type="text"
            value={policyNameFilter}
            onChange={(e) => setPolicyNameFilter(e.target.value)}
            placeholder={t('retentionAdmin.history.filter.policyNamePlaceholder')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSearch();
            }}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-sans)',
              minWidth: 200,
            }}
          />
        </label>

        {/* 상태 */}
        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-1)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {t('retentionAdmin.history.filter.status')}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={selectStyle}
          >
            <option value="">{t('retentionAdmin.history.filter.statusAll')}</option>
            <option value="SUCCESS">{t('retentionAdmin.status.SUCCESS')}</option>
            <option value="ABORTED">{t('retentionAdmin.status.ABORTED')}</option>
            <option value="FAILED">{t('retentionAdmin.status.FAILED')}</option>
            <option value="SKIPPED">{t('retentionAdmin.status.SKIPPED')}</option>
            <option value="DRY_RUN">{t('retentionAdmin.status.DRY_RUN')}</option>
          </select>
        </label>

        {/* 버튼 */}
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginLeft: 'auto' }}>
          <button
            type="button"
            onClick={handleSearch}
            disabled={isLoading}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('retentionAdmin.list.button.search')}
          </button>
          <button
            type="button"
            onClick={handleReset}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('retentionAdmin.list.button.reset')}
          </button>
        </div>
      </div>

      {/* 컨텐츠 영역 — 5상태 */}
      <div
        style={{
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          background: 'var(--color-surface-background)',
        }}
      >
        {/* loading skeleton */}
        {isLoading && (
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            aria-label={t('retentionAdmin.history.loadingAriaLabel')}
            aria-busy="true"
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {TABLE_HEADERS.map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--spacing-3) var(--spacing-4)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} cols={TABLE_HEADERS.length} />
              ))}
            </tbody>
          </table>
        )}

        {/* error */}
        {isError && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <ErrorState
              title={t('retentionAdmin.error.loadFailed')}
              message={
                (runsQuery.error as unknown as ApiError)?.code === 'AUTH-403-001'
                  ? t('retentionAdmin.error.forbidden')
                  : ((runsQuery.error as unknown as ApiError)?.message ??
                    t('retentionAdmin.error.network'))
              }
              onRetry={() => void runsQuery.refetch()}
            />
          </div>
        )}

        {/* empty */}
        {isEmpty && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <EmptyState
              title={t('retentionAdmin.history.empty.title')}
              description={t('retentionAdmin.history.empty.description')}
            />
          </div>
        )}

        {/* default — DataTable */}
        {showTable && pageData && (
          <>
            <div role="region" aria-label={t('retentionAdmin.history.tableAriaLabel')} style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 'var(--font-size-sm)',
                }}
                aria-label={t('retentionAdmin.history.tableAriaLabel')}
              >
                <thead>
                  <tr style={{ background: 'var(--color-surface-muted)' }}>
                    {TABLE_HEADERS.map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: 'left',
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-secondary)',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageData.content.map((row) => (
                    <tr
                      key={row.id}
                      style={{ transition: 'background 0.1s' }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background =
                          'var(--color-surface-muted)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background = '';
                      }}
                    >
                      {/* 시작 시각 */}
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-secondary)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatDateTime(row.startedAt)}
                      </td>

                      {/* 정책명 */}
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontWeight: 600,
                          color: 'var(--color-text-primary)',
                          fontFamily: 'var(--font-family-mono)',
                          fontSize: 'var(--font-size-xs)',
                        }}
                      >
                        {row.policyName}
                      </td>

                      {/* 유형 */}
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-secondary)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t(`retentionAdmin.history.trigger.${row.trigger}`, {
                          defaultValue: row.trigger,
                        })}
                      </td>

                      {/* 상태 */}
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <StatusPill tone={runStatusTone(row.status)}>
                          {t(`retentionAdmin.status.${row.status}`, {
                            defaultValue: row.status,
                          })}
                        </StatusPill>
                      </td>

                      {/* 매치/삭제 */}
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-secondary)',
                          whiteSpace: 'nowrap',
                          fontFamily: 'var(--font-family-mono)',
                        }}
                      >
                        {row.matched.toLocaleString()} / {row.deleted.toLocaleString()}
                      </td>

                      {/* 소요 */}
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-muted)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.durationMs != null
                          ? t('retentionAdmin.history.duration', { ms: row.durationMs.toLocaleString() })
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 페이지네이션 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: 'var(--color-surface-muted)',
                borderTop: '1px solid var(--color-surface-border)',
                flexWrap: 'wrap',
                gap: 'var(--spacing-2)',
              }}
            >
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                {t('retentionAdmin.list.pagination.showing', {
                  total: pageData.totalElements.toLocaleString(),
                  from: pageData.number * pageData.size + 1,
                  to: Math.min((pageData.number + 1) * pageData.size, pageData.totalElements),
                })}
              </span>
              <nav
                aria-label={t('retentionAdmin.list.pagination.nav')}
                style={{ display: 'inline-flex', gap: 'var(--spacing-1)' }}
              >
                <button
                  type="button"
                  disabled={pageData.first}
                  onClick={() => handlePageChange(page - 1)}
                  aria-label={t('retentionAdmin.list.pagination.prev')}
                  style={{
                    minWidth: 28,
                    height: 28,
                    padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: pageData.first ? 'not-allowed' : 'pointer',
                    opacity: pageData.first ? 0.5 : 1,
                  }}
                >
                  &lt;
                </button>
                {Array.from({ length: Math.min(pageData.totalPages, 10) }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handlePageChange(i)}
                    aria-label={t('retentionAdmin.list.pagination.page', { page: i + 1 })}
                    aria-current={i === pageData.number ? 'page' : undefined}
                    style={{
                      minWidth: 28,
                      height: 28,
                      padding: '0 var(--spacing-2)',
                      borderRadius: 'var(--radius-sm)',
                      background:
                        i === pageData.number
                          ? 'var(--color-brand-primary)'
                          : 'var(--color-surface-elevated)',
                      border: `1px solid ${
                        i === pageData.number
                          ? 'var(--color-brand-primary)'
                          : 'var(--color-surface-border)'
                      }`,
                      color:
                        i === pageData.number
                          ? 'var(--color-text-inverse)'
                          : 'var(--color-text-secondary)',
                      fontSize: 'var(--font-size-xs)',
                      cursor: 'pointer',
                      fontWeight: i === pageData.number ? 600 : 400,
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={pageData.last}
                  onClick={() => handlePageChange(page + 1)}
                  aria-label={t('retentionAdmin.list.pagination.next')}
                  style={{
                    minWidth: 28,
                    height: 28,
                    padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: pageData.last ? 'not-allowed' : 'pointer',
                    opacity: pageData.last ? 0.5 : 1,
                  }}
                >
                  &gt;
                </button>
              </nav>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (max-width: 768px) {
          table { min-width: 600px; }
        }
      `}</style>
    </div>
  );
}
